import { createFileRoute, Link } from '@tanstack/react-router'
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  CalendarDays,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Eye,
  FileText,
  GitCommitHorizontal,
  Info,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  PenLine,
  Pencil,
  RotateCcw,
  Settings2,
  Sparkles,
  SquareDashedMousePointer,
  User as UserIcon,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL, RevisionConflictError } from '@/api/client'
import {
  applyRealtimeFieldUpdate,
  extractFieldConflict,
  extractFieldConflicts,
  queryKeys,
  useCreateDocumentParty,
  useCreateDocumentShare,
  useCreateDocumentShareLink,
  useDocument,
  useDocumentAudit,
  useDocumentJobs,
  useDocumentParties,
  useDocumentRevision,
  useDocumentRevisions,
  useDocumentShares,
  useDocumentShareLinks,
  useDocumentTracking,
  useUpdateDocument,
  useDocumentWorkspaceContext,
  useIsReadOnly,
  useApplyAllFieldSuggestions,
  usePatchFields,
  usePatchSingleField,
  useRenderDocument,
  useRetryDocument,
  useRevokeDocumentShare,
  useRevokeDocumentShareLink,
  useWorkspaceMembers,
  useWorkspaceContext,
  type DocumentAccessEventRead,
  type DocumentPartyRead,
  type DocumentPartyRole,
  type DocumentShareLinkRead,
  type DocumentShareRead,
  type WorkspaceMemberRead,
  type WorkspaceRole,
} from '@/api/queries'
import {
  fieldDisplayName,
  isCheckboxChecked,
  MIME_DOCX,
  MIME_PDF,
  progressStageLabel,
  type DocumentDetail,
  type EditActor,
  type Field,
  type FieldConflictPayload,
  type FieldEdit,
  type OutputFormat,
  type PatchSingleFieldBody,
  type PreviewMode,
  type RevisionActor,
  type RevisionConflict,
  type RevisionSummary,
} from '@/api/docfiller-types'
import { ChatPanel } from '@/components/docfiller/Chat'
import { FieldConflictDialog } from '@/components/docfiller/FieldConflictDialog'
import {
  AiActionTray,
  fieldsWithPendingSuggestions,
  useFieldEditHandlers,
} from '@/components/docfiller/ai-actions'
import { ProcessingProgress } from '@/components/docfiller/ProcessingProgress'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { IconButton } from '@/components/ui/icon-button'
import { InlineError, LoadingState, SkeletonList } from '@/components/ui/loading-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useLivePreview } from '@/hooks/useLivePreview'
import { useRealtimeDocument, type RealtimeFieldUpdate } from '@/hooks/useRealtimeDocument'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { useUser } from '@/hooks/useUser'
import { useCurrentWorkspaceOrNull } from '@/api/workspace-context-provider'
import { useDocumentCollab } from '@/hooks/useDocumentCollab'
import { CollabPresence } from '@/components/docfiller/CollabPresence'
import { cn } from '@/lib/utils'
import { formatLocalDateTime } from '@/lib/format-date'
import { roleSatisfies } from '@/lib/workspace-roles'
import ErrorFallback from '@/components/ErrorFallback'
import { subscribeSSE } from '@/api/sse'
import type { PartyStatus, ShareFlowLink, ShareFlowParty } from '@/api/share-flow'

import {
  AiEditBulkButton,
  AiEditFieldButton,
} from '@/components/docfiller/AiEditMenu'
import { AiSuggestionsPanel } from '@/components/docfiller/AiSuggestionsPanel'
import { ContextPrecedencePanel } from '@/components/docfiller/ContextPrecedencePanel'
import { DocumentContextPanel } from '@/components/docfiller/DocumentContextPanel'
import { DocumentReusableContextPanel } from '@/components/docfiller/DocumentReusableContextPanel'
import { EffectiveInstructionsPanel } from '@/components/docfiller/EffectiveInstructionsPanel'
import { buildFieldOverlayRenderer } from '@/components/docfiller/fieldOverlayBuilder'
import { useFieldEditing, type FieldEditingApi } from '@/components/docfiller/useFieldEditing'
import { DocumentViewer } from '@/components/docfiller/viewer'
import {
  ReviewFocusProvider,
  ReviewOverlay,
  ReviewPanel,
  useReviewFocus,
} from '@/components/docfiller/review'
import {
  useDocumentComments,
  useCreateDocumentField,
  useDocumentSuggestions,
  usePatchFieldDefinition,
} from '@/api/queries'
import { groupReview } from '@/components/docfiller/review/types'
import {
  FieldPlacementPerPage,
  type PlacementTool,
} from '@/components/docfiller/FieldPlacementLayer'

const DocxPreview = lazy(() => import('@/components/docfiller/DocxPreview'))

export const Route = createFileRoute('/_authenticated/docfiller/$documentId')({
  component: DocfillerDetailPage,
  errorComponent: ErrorFallback,
})

/**
 * Tabs exposed in the work-area tabstrip.
 *
 * On desktop the document preview lives in a permanent left pane so the
 * tabs cover the remaining work-area panels: Fields, Context, Review, Chat,
 * Versions & audit, Sharing. On mobile the document is the default full-height
 * surface and the top-right overflow button switches into the secondary work
 * panels.
 *
 * `value` is stable across viewports — the tablist only varies in *which*
 * triggers it renders. This means an open tab survives a viewport flip,
 * and the SheetTabTriggers can hand off to the main TabsList by simply
 * setting the same value.
 */
type WorkTab =
  | 'document'
  | 'fields'
  | 'context'
  | 'review'
  | 'chat'
  | 'versions'
  | 'sharing'

const MOBILE_OVERFLOW_TABS: ReadonlyArray<WorkTab> = [
  'document',
  'fields',
  'chat',
  'context',
  'review',
  'versions',
  'sharing',
]
const DESKTOP_TABS: ReadonlyArray<WorkTab> = [
  'fields',
  'context',
  'review',
  'chat',
  'versions',
  'sharing',
]

function tabLabel(tab: WorkTab): string {
  switch (tab) {
    case 'document':
      return 'Document'
    case 'fields':
      return 'Fields'
    case 'context':
      return 'Context'
    case 'review':
      return 'Review'
    case 'chat':
      return 'Chat'
    case 'versions':
      return 'Versions & audit'
    case 'sharing':
      return 'Sharing'
  }
}


export function DocfillerDetailPage() {
  const { documentId } = Route.useParams()
  const { data: doc, isLoading, error } = useDocument(documentId)
  const qc = useQueryClient()
  const isDesktop = useIsDesktop()
  // Backend ``is_active`` is the source of truth for write access. Hoisted
  // here so a single hook call drives the page banner + every child panel.
  const readOnly = useIsReadOnly()
  const { user } = useUser()
  const workspaceCtx = useCurrentWorkspaceOrNull()
  const collab = useDocumentCollab({
    documentId,
    display: user?.name ?? user?.email ?? null,
  })
  // Editing state is lifted here so the fields panel and the on-document
  // overlay layer share one dirty buffer + selection + per-field save
  // status. A value typed in the panel shows up immediately on the
  // overlay (and vice versa) and the panel's "Save changes" / "Apply all"
  // buttons stay the bulk-save path for users who prefer the form view.
  const editing = useFieldEditing()
  // Default the overlay layer on for PDFs so the new flow is the first
  // thing the user sees; image/DOCX docs don't have per-field rect
  // anchors so the toggle just hides a always-empty layer.
  const [showOverlays, setShowOverlays] = useState(true)

  useRealtimeDocument({
    documentId,
    onFieldUpdate: useCallback(
      (field: RealtimeFieldUpdate, docRev: number) => {
        applyRealtimeFieldUpdate(qc, documentId, field, docRev)
      },
      [qc, documentId],
    ),
    onCommentEvent: useCallback(() => {
      qc.invalidateQueries({ queryKey: ['comments', 'list', documentId] })
    }, [qc, documentId]),
  })

  // Fields is the primary work surface on landing on desktop — every
  // documented user flow (apply suggestion, edit + save, export) starts
  // there, and the existing e2e suite asserts the field list is visible
  // as soon as the page mounts. On mobile the preview is a tab (not a
  // permanent pane), so landing on "fields" hides the just-uploaded
  // document under display:none — the dogfood smoke caught this as
  // "preview never rendered". We pick the default lazily during render
  // (instead of an effect) so the first paint already has the right tab
  // and avoid an extra setState/re-render after mount.
  const [storedTab, setStoredTab] = useState<WorkTab | null>(null)
  const [overflowOpen, setOverflowOpen] = useState(false)

  // The "Document" tab only exists on mobile. When the viewport flips to
  // desktop the preview moves to its own pane and the value would be
  // orphaned — derive a sanitised tab during render so the fields tab is
  // shown instead. Done as derived state (not an effect) so React gets a
  // single consistent snapshot per render. On mobile we default to the
  // "document" tab so the preview is visible immediately after upload.
  const defaultTab: WorkTab = isDesktop ? 'fields' : 'document'
  const tab: WorkTab =
    storedTab === null
      ? defaultTab
      : isDesktop && storedTab === 'document'
        ? 'fields'
        : storedTab
  const setTab = setStoredTab

  const documentRole = doc?.workspace_uuid
    ? (workspaceCtx?.workspaces.find((workspace) => workspace.uuid === doc.workspace_uuid)
        ?.role ?? null)
    : (workspaceCtx?.currentRole ?? null)
  const aiEditReadOnly = !documentRole || !roleSatisfies(documentRole, 'editor')

  if (isLoading) {
    return (
      <div className="flex h-[calc(100svh-4rem)] items-center justify-center">
        <LoadingState title="Loading document…" description="Fetching fields and preview." />
      </div>
    )
  }

  if (error || !doc) {
    const accessHint =
      'You may not have permission to access this document, or it may have been deleted.'
    const detailMessage = error instanceof Error
      ? `${accessHint} ${error.message}`
      : accessHint

    return (
      <div
        className="mx-auto w-full max-w-3xl space-y-4 px-4 py-8 md:px-8"
        role="alert"
        data-testid="document-detail-error"
      >
        <BackLink />
        <InlineError
          title="Document not found"
          message={detailMessage}
        />
      </div>
    )
  }

  if (!isDesktop && isBlockingMobileProcessing(doc)) {
    return <MobileDocumentLoadingScreen doc={doc} readOnly={readOnly} />
  }

  return (
    <ReviewFocusProvider>
      <div
        className="flex min-h-[calc(100svh-3rem)] flex-col md:h-[calc(100vh-4rem)]"
        data-testid="docfiller-detail-page"
      >
        <DetailHeader
          doc={doc}
          showOverflowTrigger={!isDesktop}
          onOpenMore={() => setOverflowOpen(true)}
          showOverlays={showOverlays}
          onToggleOverlays={() => setShowOverlays((v) => !v)}
          collab={collab}
          canRename={!readOnly && !aiEditReadOnly}
        />
        {readOnly && (
          <div
            role="status"
            data-testid="docfiller-detail-readonly-banner"
            className="flex items-start gap-2 border-b border-border bg-muted/60 px-4 py-2 text-xs text-muted-foreground md:px-6"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span>
              Your account is read-only. You can view this document and its
              audit history, but field edits, context changes, and chat
              instructions are disabled.
            </span>
          </div>
        )}
        <TemplateFillStreamResumer doc={doc} />
        <ProcessingBanner doc={doc} readOnly={readOnly} />
        {!isDesktop && <FillingStatusStrip doc={doc} mobile />}

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as WorkTab)}
          className="flex min-h-0 flex-1 flex-col gap-0 md:grid md:grid-cols-[minmax(0,_3fr)_minmax(0,_2fr)] md:grid-rows-[1fr]"
        >
          {/* Desktop: persistent preview pane to the left of the tab panels.
              Gated on `isDesktop` (not just `md:hidden`) so the test layer —
              which can't evaluate Tailwind — doesn't see duplicate document
              viewers when a mobile spec is asserting the in-tab preview. */}
          {isDesktop && (
            <DesktopPreviewPane
              doc={doc}
              editing={editing}
              showOverlays={showOverlays}
              readOnly={readOnly}
            />
          )}

          <div
            className={cn(
              'flex min-h-0 min-w-0 flex-col bg-background md:border-l md:border-border',
            )}
            data-testid="work-area"
          >
            {isDesktop ? (
              <div className="flex items-start gap-3 px-4 pt-3 md:px-5 md:pt-4">
                <TabsList
                  aria-label="Document workspace"
                  wrap
                  className="justify-start"
                >
                  {DESKTOP_TABS.map((t) => (
                    <TabsTrigger
                      key={t}
                      value={t}
                      data-testid={
                        t === 'chat'
                          ? 'chat-history-tab'
                          : t === 'review'
                            ? 'review-tab'
                            : t === 'versions'
                              ? 'versions-tab'
                              : undefined
                      }
                    >
                      {tabLabel(t)}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <FillingStatusStrip doc={doc} />
              </div>
            ) : null}

            <div
              className={cn(
                'min-h-0 flex-1 overflow-y-auto md:px-5 md:pb-6',
                !isDesktop && tab === 'document'
                  ? 'px-0 pb-0'
                  : 'px-4 pb-4',
              )}
              data-testid="work-area-scroll"
            >
              <TabsContent
                value="document"
                className={cn('h-full', isDesktop ? 'hidden' : 'pt-0')}
                data-testid="tab-panel-document"
              >
                <MobilePreviewBody
                  doc={doc}
                  editing={editing}
                  showOverlays={showOverlays}
                  readOnly={readOnly}
                />
              </TabsContent>
              <TabsContent value="fields" className="pt-3" data-testid="tab-panel-fields">
                <FieldsPanel
                  doc={doc}
                  editing={editing}
                  readOnly={readOnly}
                  aiEditReadOnly={aiEditReadOnly}
                />
              </TabsContent>
              <TabsContent value="context" className="pt-3" data-testid="tab-panel-context">
                <ContextTabContent doc={doc} readOnly={readOnly} />
              </TabsContent>
              <TabsContent value="review" className="pt-3" data-testid="tab-panel-review">
                <ReviewTabContent doc={doc} readOnly={readOnly} />
              </TabsContent>
              <TabsContent
                value="chat"
                // Flex column so ChatPanel (flex-1) fills the available
                // work-area-scroll height exactly — the previous
                // ``h-full pt-3`` combined with the panel's own
                // ``md:h-[calc(100vh-11rem)]`` overshot the parent by
                // ~29px (more once the old route-level fill banner mounted), pushing the
                // composer below the viewport on desktop.
                className="flex h-full min-h-0 flex-col pt-3"
                data-testid="tab-panel-chat"
              >
                <ChatPanel key={doc.uuid} doc={doc} readOnly={readOnly} />
              </TabsContent>
              <TabsContent value="versions" className="pt-3" data-testid="tab-panel-versions">
                <VersionsPanel doc={doc} />
              </TabsContent>
              <TabsContent value="sharing" className="pt-3" data-testid="tab-panel-sharing">
                <SharingPanel doc={doc} documentRole={documentRole} />
              </TabsContent>
            </div>
          </div>

          <MobileOverflowSheet
            open={overflowOpen}
            onOpenChange={setOverflowOpen}
            activeTab={tab}
            onSelect={(next) => {
              setTab(next)
              setOverflowOpen(false)
            }}
          />
        </Tabs>
      </div>
    </ReviewFocusProvider>
  )
}

function isBlockingMobileProcessing(doc: DocumentDetail): boolean {
  return doc.status === 'uploaded' || doc.status === 'parsing' || doc.status === 'filling'
}

function documentTitle(doc: DocumentDetail): string {
  return doc.display_name?.trim() || doc.source_filename
}

function MobileDocumentLoadingScreen({
  doc,
  readOnly,
}: {
  doc: DocumentDetail
  readOnly: boolean
}) {
  const retry = useRetryDocument(doc.uuid)
  const jobs = useDocumentJobs(doc.uuid)

  const onRetry = async () => {
    if (readOnly) return
    try {
      await retry.mutateAsync()
      toast.success('Retrying — we will let you know when it finishes.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed')
    }
  }

  return (
    <div
      className="flex min-h-[calc(100svh-3rem)] flex-col bg-background"
      data-testid="mobile-document-loading-screen"
    >
      <div className="sticky top-0 z-20 flex min-h-11 items-center border-b border-border/70 bg-background/95 px-3 py-1 backdrop-blur">
        <BackLink />
      </div>
      <main className="flex flex-1 items-center justify-center px-4 py-6">
        <div className="w-full max-w-sm space-y-4">
          <div className="space-y-1 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Preparing document
            </p>
            <h1 className="truncate text-lg font-semibold tracking-tight" title={documentTitle(doc)}>
              {documentTitle(doc)}
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground">
              The document will open when upload processing and field extraction are complete.
            </p>
          </div>
          <ProcessingProgress
            doc={doc}
            latestJob={jobs.data?.[0] ?? null}
            onRetry={readOnly ? undefined : onRetry}
            isRetrying={retry.isPending}
          />
        </div>
      </main>
    </div>
  )
}

function TemplateFillStreamResumer({ doc }: { doc: DocumentDetail }) {
  const qc = useQueryClient()
  const activeRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (
      doc.status !== 'filling' ||
      !doc.template_source_uuid ||
      activeRef.current
    ) {
      return
    }

    const ctl = new AbortController()
    activeRef.current = ctl
    const detailKey = queryKeys.documents.detail(doc.uuid)
    const url = `${API_BASE_URL}/dashboard/documents/${doc.uuid}/fill/stream`

    void subscribeSSE(url, {
      signal: ctl.signal,
      onEvent: (event) => {
        if (event.event === 'field_proposal') {
          qc.invalidateQueries({ queryKey: detailKey })
          return
        }
        if (event.event === 'done') {
          ctl.abort()
          activeRef.current = null
          qc.invalidateQueries({ queryKey: detailKey })
          qc.invalidateQueries({ queryKey: queryKeys.documents.all })
          return
        }
        if (event.event === 'error') {
          let message = 'Autofill failed'
          try {
            const parsed = JSON.parse(event.data) as { message?: unknown }
            if (typeof parsed.message === 'string' && parsed.message.trim()) {
              message = parsed.message
            }
          } catch {
            // Keep the fixed fallback if the SSE data is malformed.
          }
          toast.error(message)
          ctl.abort()
          activeRef.current = null
          qc.invalidateQueries({ queryKey: detailKey })
        }
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : String(err)
        toast.error(`Autofill stream failed: ${message}`)
        activeRef.current = null
        qc.invalidateQueries({ queryKey: detailKey })
      },
    })

    return () => {
      ctl.abort()
      activeRef.current = null
    }
  }, [doc.status, doc.template_source_uuid, doc.uuid, qc])

  return null
}

// `ReviewPanel` needs the viewer's current page (so a newly-anchored
// comment binds to what the user is actually looking at). That page lives
// in `ReviewFocusProvider`, which wraps the route — so we read it here in
// a thin shim rather than threading it through `DocfillerDetailPage`.
function ReviewTabContent({
  doc,
}: {
  doc: DocumentDetail
  readOnly: boolean
}) {
  const focus = useReviewFocus()
  return (
    <div className="space-y-4">
      <section
        className="space-y-3 rounded-lg border border-border bg-card p-3"
        aria-labelledby="user-review-heading"
      >
        <div className="space-y-1">
          <h2 id="user-review-heading" className="text-sm font-semibold tracking-tight">
            User review
          </h2>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Comments and anchored notes from people reviewing this document.
          </p>
        </div>
        <ReviewPanel doc={doc} currentPage={focus.currentViewerPage} />
      </section>

      <section
        className="space-y-3 rounded-lg border border-border bg-card p-3"
        aria-labelledby="ai-review-heading"
        data-testid="review-ai-suggestions-section"
      >
        <div className="space-y-1">
          <h2 id="ai-review-heading" className="text-sm font-semibold tracking-tight">
            AI suggestions
          </h2>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Agent proposals stay separate from user comments until you accept
            or reject them.
          </p>
        </div>
        <AiSuggestionsPanel documentId={doc.uuid} />
      </section>
    </div>
  )
}

// ─────────────────────────── Header ──────────────────────────────────────

function DetailHeader({
  doc,
  showOverflowTrigger,
  onOpenMore,
  showOverlays,
  onToggleOverlays,
  collab,
  canRename,
}: {
  doc: DocumentDetail
  showOverflowTrigger: boolean
  onOpenMore: () => void
  showOverlays: boolean
  onToggleOverlays: () => void
  collab: ReturnType<typeof useDocumentCollab>
  canRename: boolean
}) {
  const updateDocument = useUpdateDocument(doc.uuid)
  const [renameOpen, setRenameOpen] = useState(false)
  const [name, setName] = useState(documentTitle(doc))
  const rename = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      await updateDocument.mutateAsync({ display_name: trimmed })
      toast.success('Document renamed')
      setRenameOpen(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not rename document')
    }
  }
  return (
    <div
      className="sticky top-0 z-20 flex min-h-11 items-center justify-between gap-2 border-b border-border/70 bg-background/90 px-2 py-1 backdrop-blur md:static md:min-h-0 md:gap-3 md:px-6 md:py-3"
      data-testid="detail-header"
    >
      <div className="min-w-0 space-y-0.5">
        <BackLink />
        <div className="hidden min-w-0 items-center gap-1 md:flex">
          <h1
            className="truncate text-base font-semibold tracking-tight"
            title={documentTitle(doc)}
          >
            {documentTitle(doc)}
          </h1>
          {canRename && (
            <IconButton
              label="Rename document"
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                setName(documentTitle(doc))
                setRenameOpen(true)
              }}
            >
              <Pencil />
            </IconButton>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <CollabPresence
          enabled={collab.enabled}
          presence={collab.presence}
          selfSessionId={collab.selfSessionId}
          connectionState={collab.connectionState}
          lastError={collab.lastError}
        />
        {/* Overlay toggle — only meaningful on PDFs (image/DOCX have no
            per-field rect anchors), but we show it always so the
            affordance stays in the same place. */}
        <Button
          type="button"
          size="sm"
          variant={showOverlays ? 'soft' : 'outline'}
          aria-pressed={showOverlays}
          onClick={onToggleOverlays}
          data-testid="toggle-field-overlays"
          className="size-9 px-0 md:size-auto md:px-3"
          title={
            showOverlays
              ? 'Hide field highlights on the document'
              : 'Show field highlights on the document'
          }
        >
          <SquareDashedMousePointer className="size-4" />
          <span className="hidden md:inline">
            {showOverlays ? 'Overlays on' : 'Overlays off'}
          </span>
        </Button>
        <DownloadDialog doc={doc} />
        {/* Mobile-only entry into the secondary document actions overflow.
            On desktop those tabs already sit in the main tablist, so this
            CTA is redundant chrome. Gated by JS rather than `md:hidden`
            so tests (which can't read Tailwind) see the same shape the
            user does. */}
        {showOverflowTrigger && (
          <IconButton
            label="More document actions"
            variant="outline"
            size="icon-sm"
            onClick={onOpenMore}
            data-testid="open-overflow-sheet"
          >
            <MoreHorizontal />
          </IconButton>
        )}
      </div>
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename document</DialogTitle>
          </DialogHeader>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Document name"
            maxLength={255}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void rename()} disabled={updateDocument.isPending || !name.trim()}>
              {updateDocument.isPending && <Loader2 className="animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ProcessingBanner({
  doc,
  readOnly = false,
}: {
  doc: DocumentDetail
  readOnly?: boolean
}) {
  const retry = useRetryDocument(doc.uuid)
  const visible = doc.status === 'error'
  const jobs = useDocumentJobs(visible ? doc.uuid : undefined)
  if (!visible) return null

  const onRetry = async () => {
    if (readOnly) return
    try {
      await retry.mutateAsync()
      toast.success('Retrying — we will let you know when it finishes.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed')
    }
  }

  const latestJob = jobs.data?.[0] ?? null

  return (
    <div className="border-b border-border bg-background px-4 py-3 md:px-6">
      <ProcessingProgress
        doc={doc}
        latestJob={latestJob}
        // Omit ``onRetry`` for read-only callers so ProcessingProgress's
        // ``canRetry`` gate hides the retry button entirely — no need to
        // also render a disabled button.
        onRetry={readOnly ? undefined : onRetry}
        isRetrying={retry.isPending}
      />
    </div>
  )
}

/**
 * Compact "AI fill in progress" strip.
 *
 * Desktop renders this inside the right-pane tab chrome so starting a fill
 * does not insert a new block above the document/chat split. Mobile keeps a
 * small route-level strip because the preview and panels are single-column.
 *
 * The two staging smokes (the dogfood Playwright spec and the parent
 * live_expansion_smoke.mjs script) both wait for
 * ``data-testid="ai-filled-banner"`` as one acceptable signal that the
 * POST /fill path is live; keep that test id mounted outside the Fields tab.
 *
 * The post-fill summary banner inside ``FieldsPanel`` keeps its existing
 * UX (count + edit affordance) and shares the same testid; the two are
 * mutually exclusive in time (filling → filled), so the smokes never
 * have to pick between them.
 */
function FillingStatusStrip({
  doc,
  mobile = false,
}: {
  doc: DocumentDetail
  mobile?: boolean
}) {
  if (doc.status !== 'filling') return null
  const percent =
    typeof doc.progress_percent === 'number' ? doc.progress_percent : null
  const stage = progressStageLabel(doc.progress_stage)
  return (
    <div
      className={cn(
        mobile
          ? 'border-b border-border bg-background px-4 py-2'
          : 'hidden w-64 shrink-0 md:block',
      )}
    >
      <div
        className={cn(
          'rounded-md border border-info-soft bg-info-soft/60 px-3 py-2 text-info-soft-foreground',
          mobile ? 'text-xs' : 'text-[11px] shadow-soft',
        )}
        data-testid="ai-filled-banner"
        data-state="filling"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-2">
          <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-semibold">{stage}</span>
              {percent !== null && (
                <span className="shrink-0 tabular-nums text-info-soft-foreground/80">
                  {percent}%
                </span>
              )}
            </div>
            <Progress
              percent={percent}
              size="sm"
              tone="success"
              className="mt-1"
              data-testid="ai-fill-progress-bar"
            />
          </div>
        </div>
        {mobile && (
          <p className="mt-1.5 leading-relaxed">
            Proposals will appear in chat and on the Fields tab as the agent
            works.
          </p>
        )}
      </div>
    </div>
  )
}

function BackLink() {
  return (
    <Link
      to="/docfiller"
      className="inline-flex min-h-8 items-center gap-1.5 px-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground md:min-h-0 md:px-0"
      aria-label="Back to all documents"
    >
      <ArrowLeft className="size-3" />
      <span className="hidden min-[380px]:inline">All documents</span>
    </Link>
  )
}

function MobileOverflowSheet({
  open,
  onOpenChange,
  activeTab,
  onSelect,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  activeTab: WorkTab
  onSelect: (next: WorkTab) => void
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl border-t border-border bg-background p-0"
        data-testid="mobile-overflow-sheet"
        aria-describedby={undefined}
      >
        <SheetHeader className="border-b border-border px-5 py-3 text-left">
          <SheetTitle>…</SheetTitle>
        </SheetHeader>
        <div
          className="grid grid-cols-1 divide-y divide-border pb-[calc(1rem+env(safe-area-inset-bottom))]"
          role="menu"
        >
          {MOBILE_OVERFLOW_TABS.map((t) => {
            const isActive = activeTab === t
            return (
              <button
                key={t}
                type="button"
                role="menuitem"
                onClick={() => onSelect(t)}
                className={cn(
                  'flex items-center justify-between gap-3 px-5 py-4 text-left text-sm font-medium transition-colors',
                  'min-h-12 hover:bg-accent/60',
                  'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 focus-visible:ring-inset',
                  isActive && 'bg-primary-soft/60 text-foreground',
                )}
                data-testid={`mobile-overflow-${t}`}
              >
                <span className="flex items-center gap-2.5">
                  {t === 'document' && <FileText className="size-4" aria-hidden />}
                  {t === 'fields' && <SquareDashedMousePointer className="size-4" aria-hidden />}
                  {t === 'chat' && <MessageSquare className="size-4" aria-hidden />}
                  {t === 'context' && <Settings2 className="size-4" aria-hidden />}
                  {t === 'review' && (
                    <CheckCircle2
                      className="size-4"
                      aria-hidden
                      data-testid="mobile-overflow-review-icon"
                    />
                  )}
                  {t === 'versions' && <GitCommitHorizontal className="size-4" aria-hidden />}
                  {t === 'sharing' && <MessageSquare className="size-4" aria-hidden />}
                  {tabLabel(t)}
                </span>
                {isActive && (
                  <Badge tone="primary" size="xs">
                    Open
                  </Badge>
                )}
              </button>
            )
          })}
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ─────────────────────────── Preview panes ──────────────────────────────

function DesktopPreviewPane({
  doc,
  editing,
  showOverlays,
  readOnly,
}: {
  doc: DocumentDetail
  editing: FieldEditingApi
  showOverlays: boolean
  readOnly: boolean
}) {
  return (
    <div
      className="hidden min-h-0 min-w-0 bg-muted/40 md:flex md:flex-col"
      data-testid="desktop-preview-pane"
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 md:px-6">
        <PreviewBody
        doc={doc}
        editing={editing}
        showOverlays={showOverlays}
        readOnly={readOnly}
        fillHeight
      />
      </div>
    </div>
  )
}

function MobilePreviewBody({
  doc,
  editing,
  showOverlays,
  readOnly,
}: {
  doc: DocumentDetail
  editing: FieldEditingApi
  showOverlays: boolean
  readOnly: boolean
}) {
  return (
    <div className="h-full md:hidden">
      <PreviewBody
        doc={doc}
        editing={editing}
        showOverlays={showOverlays}
        readOnly={readOnly}
        fillHeight
        initialFitMode="page"
      />
    </div>
  )
}

function PreviewBody({
  doc,
  editing,
  showOverlays,
  readOnly,
  fillHeight,
  initialFitMode = 'width',
}: {
  doc: DocumentDetail
  editing: FieldEditingApi
  showOverlays: boolean
  readOnly: boolean
  fillHeight: boolean
  initialFitMode?: 'page' | 'width'
}) {
  const [mode, setMode] = useState<PreviewMode>('filled')
  const preview = useLivePreview(doc, mode)
  const commentsQ = useDocumentComments(doc.uuid)
  const suggestionsQ = useDocumentSuggestions(doc.uuid)
  const createField = useCreateDocumentField(doc.uuid)
  const patchFieldDefinition = usePatchFieldDefinition(doc.uuid)
  const [placementTool, setPlacementTool] = useState<PlacementTool | null>(null)
  const review = useMemo(
    () => groupReview(commentsQ.data ?? [], suggestionsQ.data ?? []),
    [commentsQ.data, suggestionsQ.data],
  )
  const focus = useReviewFocus()
  const singlePatch = usePatchSingleField(doc.uuid)
  const savePatch = useCallback(
    (fieldId: string, body: Parameters<typeof singlePatch.mutateAsync>[0]['body']) =>
      singlePatch.mutateAsync({ fieldId, body }),
    [singlePatch],
  )
  const fieldOverlayRenderer = useMemo(
    () =>
      showOverlays
        ? buildFieldOverlayRenderer({
            fields: doc.fields,
            editing,
            savePatch,
          })
        : undefined,
    [showOverlays, doc.fields, editing, savePatch],
  )

  // Translate the panel/overlay's "jump to this thread or suggestion" intent
  // into a viewer-level page jump. We pass `null` until the user actually
  // raises a jump so the viewer's initial scroll position (page 1) isn't
  // overridden on mount.
  const pageJump = useMemo(
    () =>
      focus.pageJumpTarget != null
        ? { pageNumber: focus.pageJumpTarget, token: focus.pageJumpToken }
        : null,
    [focus.pageJumpTarget, focus.pageJumpToken],
  )

  const previewUrl = preview.sourceUrl
  const viewerUrl = previewUrl ?? doc.presigned_url ?? ''

  if (!preview.data && !previewUrl) {
    if (doc.status === 'uploaded' || doc.status === 'parsing') {
      return (
        <Card
          className="flex h-64 flex-col items-center justify-center gap-2 text-sm text-muted-foreground"
          data-testid="preview-processing"
        >
          <Loader2 className="size-6 animate-spin" aria-hidden />
          <div>Preview will appear once processing finishes.</div>
        </Card>
      )
    }
    return (
      <Card className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Preview not available for this document.
      </Card>
    )
  }
  const isPdf = doc.mime_type === MIME_PDF
  const isDocx = doc.mime_type === MIME_DOCX
  const isImage = doc.mime_type.startsWith('image/')
  if (!isPdf && !isDocx && !isImage) {
    return (
      <Card className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Unsupported file type: {doc.mime_type}
      </Card>
    )
  }

  // Hand the in-memory Blob to the preview component once the live render
  // resolves. Until then, fall back to the R2 source presigned URL so the
  // pane isn't blank during first load. Feeding the Blob (rather than a
  // ``blob:`` URL string) avoids pdf.js GET-fetching the blob URL from
  // inside its worker — that path 405s on some staging proxy configs and
  // surfaces as "Failed to load PDF." The DocxPreview / DocumentViewer
  // surfaces both accept a Blob source directly.
  const previewSource: Blob | string = preview.data ?? previewUrl ?? viewerUrl
  const fallback = (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  )

  return (
    <div
      className="relative h-full md:[&_[data-testid=viewer-toolbar]]:pl-[14rem]"
      data-testid="preview-body"
    >
      <PreviewModeControl mode={mode} onModeChange={setMode} />
      <div
        className={cn(
          'h-full transition-[filter,opacity] duration-200',
          preview.isUpdating && preview.data && 'opacity-90 blur-[1px]',
        )}
        data-testid="preview-continuity-layer"
      >
        {isDocx ? (
          <Suspense fallback={fallback}>
            <DocxPreview source={previewSource} />
          </Suspense>
        ) : (
        <div
          className={cn(
            fillHeight
              ? 'h-full min-h-[calc(100svh-5.75rem)] md:h-[calc(100vh-9rem)] md:min-h-[28rem]'
              : 'h-[calc(100svh-16rem)] min-h-[24rem] md:h-[calc(100vh-16rem)]',
          )}
        >
          <DocumentViewer
            source={{
              id: doc.uuid,
              url: viewerUrl,
              data: previewSource instanceof Blob ? previewSource : undefined,
              mimeType: doc.mime_type,
            }}
            initialFitMode={initialFitMode}
            pageJump={pageJump}
            onCurrentPageChange={focus.setCurrentViewerPage}
            renderOverlay={(args) => (
              <>
                {fieldOverlayRenderer ? fieldOverlayRenderer(args) : null}
                {showOverlays && (
                  <FieldPlacementPerPage
                    page={args.page}
                    scale={args.scale}
                    rotation={args.rotation}
                    fields={doc.fields}
                    tool={placementTool}
                    readOnly={readOnly}
                    onCreateField={createField.mutateAsync}
                    onPatchField={(fieldId, body) =>
                      patchFieldDefinition.mutateAsync({ fieldId, body })
                    }
                    onToolChange={setPlacementTool}
                  />
                )}
                <ReviewOverlay
                  pageNumber={args.page.pageNumber}
                  dims={args.page.intrinsic}
                  scale={args.scale}
                  rotation={args.rotation}
                  fields={doc.fields}
                  threads={review.threads}
                  pendingSuggestions={review.pendingSuggestions}
                  focusedUuid={focus.focused?.uuid ?? null}
                />
              </>
            )}
          />
        </div>
        )}
      </div>

      {isPdf && showOverlays && !readOnly && (
        <FieldPlacementToolbar
          tool={placementTool}
          onToolChange={setPlacementTool}
          busy={createField.isPending || patchFieldDefinition.isPending}
        />
      )}

      {preview.isUpdating && preview.data && (
        <div
          className="pointer-events-none absolute inset-0 z-10 bg-background/10"
          aria-hidden
          data-testid="preview-refresh-scrim"
        />
      )}

      {preview.isUpdating && (
        <div
          className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2"
          aria-hidden
        >
          <div className="flex items-center gap-2 rounded-full border border-border bg-background/95 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-floating">
            <Loader2 className="size-4 animate-spin" />
            Updating preview…
          </div>
        </div>
      )}
    </div>
  )
}

const PREVIEW_MODES: { value: PreviewMode; label: string; title: string }[] = [
  {
    value: 'original',
    label: 'Original',
    title: 'Blank source document as uploaded - no fields filled',
  },
  {
    value: 'filled',
    label: 'Filled',
    title: 'Saved field values rendered into the document',
  },
  {
    value: 'final',
    label: 'Final',
    title: 'Saved values plus unreviewed suggestions',
  },
]

function FieldPlacementToolbar({
  tool,
  onToolChange,
  busy,
}: {
  tool: PlacementTool | null
  onToolChange: (tool: PlacementTool | null) => void
  busy: boolean
}) {
  const buttonClass = 'size-8 px-0 md:size-auto md:px-2.5'
  return (
    <div
      className="absolute right-1.5 top-12 z-40 flex items-center gap-1 rounded-md border border-border bg-background/95 p-1 shadow-soft md:right-3 md:top-2"
      data-testid="field-placement-toolbar"
    >
      <Button
        type="button"
        size="sm"
        variant={tool === 'signature' ? 'soft' : 'outline'}
        aria-pressed={tool === 'signature'}
        disabled={busy}
        className={buttonClass}
        data-testid="place-signature-field"
        title="Place signature field"
        onClick={() => onToolChange(tool === 'signature' ? null : 'signature')}
      >
        <PenLine />
        <span className="hidden md:inline">Signature</span>
      </Button>
      <Button
        type="button"
        size="sm"
        variant={tool === 'date' ? 'soft' : 'outline'}
        aria-pressed={tool === 'date'}
        disabled={busy}
        className={buttonClass}
        data-testid="place-date-field"
        title="Place date field"
        onClick={() => onToolChange(tool === 'date' ? null : 'date')}
      >
        <CalendarDays />
        <span className="hidden md:inline">Date</span>
      </Button>
    </div>
  )
}

function PreviewModeControl({
  mode,
  onModeChange,
}: {
  mode: PreviewMode
  onModeChange: (mode: PreviewMode) => void
}) {
  return (
    <div
      className="absolute left-1.5 top-12 z-10 flex rounded-md border border-border bg-background/95 p-0.5 shadow-soft md:left-3 md:top-2 md:p-1"
      data-testid="preview-mode-control"
    >
      {PREVIEW_MODES.map((option) => (
        <button
          key={option.value}
          type="button"
          className={cn(
            'h-7 rounded px-1.5 text-[11px] font-medium text-muted-foreground transition-colors min-[390px]:px-2 min-[390px]:text-xs md:h-8 md:px-3',
            mode === option.value && 'bg-primary text-primary-foreground',
          )}
          title={option.title}
          aria-pressed={mode === option.value}
          onClick={() => onModeChange(option.value)}
          data-testid={`preview-mode-${option.value}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

// ─────────────────────────── Fields tab ──────────────────────────────────

function ContextTabContent({
  doc,
  readOnly,
}: {
  doc: DocumentDetail
  readOnly: boolean
}) {
  const { data: userScope } = useWorkspaceContext()
  const { data: documentScope } = useDocumentWorkspaceContext(doc.uuid)
  return (
    <div className="space-y-3">
      <ContextPrecedencePanel
        doc={doc}
        documentScope={documentScope}
        userScope={userScope}
        readOnly={readOnly}
      />
      <EffectiveInstructionsPanel documentId={doc.uuid} />
      <DocumentContextPanel doc={doc} readOnly={readOnly} />
      <DocumentReusableContextPanel doc={doc} readOnly={readOnly} />
    </div>
  )
}

interface FieldConflictState {
  conflict: FieldConflictPayload
  fieldLabel: string
  snapshot: Map<string, string | null>
  /** Original PATCH body that triggered the conflict — forwarded to the
   *  dialog so "Keep mine" can replay it with the updated base_revision. */
  originalBody?: PatchSingleFieldBody
}

export function FieldsPanel({
  doc,
  editing,
  readOnly = false,
  aiEditReadOnly,
}: {
  doc: DocumentDetail
  editing: FieldEditingApi
  readOnly?: boolean
  aiEditReadOnly: boolean
}) {
  const documentId = doc.uuid.toString()
  const bulkPatch = usePatchFields(documentId)
  const applyAllSuggestions = useApplyAllFieldSuggestions(documentId)
  const [activeConflict, setActiveConflict] = useState<FieldConflictState | null>(null)
  const stickyBarRef = useRef<HTMLDivElement | null>(null)
  const {
    dirty,
    hasChanges,
    markSavedValue,
    setDirty,
    setDirtyValue,
    valueFor,
  } = editing

  const groups = useMemo(() => groupFieldsByPage(doc.fields), [doc.fields])

  const aiFilledCount = useMemo(
    () => doc.fields.filter((f) => f.accepted_by === 'agent').length,
    [doc.fields],
  )

  const pendingSuggestions = useMemo(
    () =>
      doc.fields.filter(
        (f) =>
          f.suggested_value != null &&
          f.suggested_value !== '' &&
          f.suggested_value !== f.current_value,
      ),
    [doc.fields],
  )

  // Fields blank enough that the "Suggest values" bulk affordance has
  // something to do. Mirrors the backend's pre-filter for ``action=suggest``
  // (blank current_value, ignoring signatures which can't be AI-filled).
  const emptyFieldCount = useMemo(
    () =>
      doc.fields.filter(
        (f) =>
          (f.current_value == null || f.current_value.trim() === '') &&
          f.kind !== 'signature',
      ).length,
    [doc.fields],
  )

  const baseRevisionUuid = doc.latest_revision?.uuid ?? null
  const [conflict, setConflict] = useState<{
    body: RevisionConflict
    snapshot: Map<string, string | null>
  } | null>(null)

  const doSave = async (
    snapshot: Map<string, string | null>,
    baseUuid: string | null,
    forceOverwrite: boolean,
  ) => {
    const body = {
      fields: Array.from(snapshot.entries()).map(([uuid, current_value]) => ({
        uuid,
        current_value,
      })),
      ...(baseUuid ? { base_revision_uuid: baseUuid } : {}),
      ...(forceOverwrite ? { force_overwrite: true } : {}),
    }
    await bulkPatch.mutateAsync(body)
  }

  const onSave = async () => {
    if (readOnly || !hasChanges) return
    // Snapshot the dirty buffer before clearing so we can roll back on error.
    // Clearing up-front (paired with the mutation's optimistic cache update)
    // makes the "unsaved" indicator disappear the instant the user clicks
    // Save — no waiting on the round-trip for visible feedback.
    const snapshot = new Map(dirty)
    for (const [uuid, value] of snapshot) {
      markSavedValue(uuid, value)
    }
    setDirty(() => new Map())
    try {
      await doSave(snapshot, baseRevisionUuid, false)
      toast.success('Saved field changes')
    } catch (e) {
      // Restore the dirty buffer so the user's edits aren't lost. Merge
      // under any fresh edits the user typed during the in-flight save.
      setDirty((prev) => {
        const next = new Map(prev)
        for (const [uuid, value] of snapshot) {
          if (!next.has(uuid)) next.set(uuid, value)
        }
        return next
      })
      if (e instanceof RevisionConflictError) {
        // Document-level conflict (PR #130): stash the snapshot + 409 body
        // so the overwrite dialog can render and a confirmed retry can
        // re-issue the save with force_overwrite=true.
        setConflict({ body: e.body as RevisionConflict, snapshot })
        return
      }
      // Per-field conflict (staging realtime collab): surface the first
      // conflicting field so the user can keep-mine / accept-theirs.
      const fieldConflicts = extractFieldConflicts(e)
      if (fieldConflicts.length > 0 && fieldConflicts[0]) {
        const conflict = fieldConflicts[0]
        const field = doc.fields.find(
          (f) => String(f.uuid) === String(conflict.field_uuid),
        )
        setActiveConflict({
          conflict,
          fieldLabel: field ? fieldDisplayName(field) : 'field',
          snapshot,
        })
        return
      }
      toast.error(e instanceof Error ? e.message : 'Save failed')
    }
  }

  const onConfirmOverwrite = async () => {
    if (!conflict) return
    const { snapshot, body: conflictBody } = conflict
    setConflict(null)
    try {
      // Use the conflict response's current_revision UUID — re-reading
      // doc.latest_revision here can hand us a stale uuid (the cache
      // invalidation kicked off by the 409 may not have refetched yet),
      // which would 409 again on the forced retry.
      await doSave(snapshot, conflictBody.current_revision.uuid, true)
      toast.success('Saved (overwriting concurrent changes)')
    } catch (e) {
      setDirty((prev) => {
        const next = new Map(prev)
        for (const [uuid, value] of snapshot) {
          if (!next.has(uuid)) next.set(uuid, value)
        }
        return next
      })
      toast.error(e instanceof Error ? e.message : 'Save failed')
    }
  }

  const onCancelOverwrite = () => {
    if (!conflict) return
    // Restore the user's edits to dirty so they can decide what to do.
    const { snapshot } = conflict
    setDirty((prev) => {
      const next = new Map(prev)
      for (const [uuid, value] of snapshot) {
        if (!next.has(uuid)) next.set(uuid, value)
      }
      return next
    })
    setConflict(null)
  }

  const onApplyAllSuggestions = async () => {
    if (readOnly || pendingSuggestions.length === 0 || applyAllSuggestions.isPending) return
    try {
      await applyAllSuggestions.mutateAsync()
      toast.success(
        `Applied ${pendingSuggestions.length} suggestion${
          pendingSuggestions.length === 1 ? '' : 's'
        }`,
      )
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Apply all failed')
    }
  }

  const onConflict = useCallback(
    (conflict: FieldConflictPayload, originalBody?: PatchSingleFieldBody) => {
      const field = doc.fields.find((f) => String(f.uuid) === String(conflict.field_uuid))
      setActiveConflict({
        conflict,
        fieldLabel: field ? fieldDisplayName(field) : 'field',
        snapshot: new Map(dirty),
        originalBody,
      })
    },
    [doc.fields, dirty],
  )

  if (doc.fields.length === 0) {
    const isStillParsing = doc.status === 'uploaded' || doc.status === 'parsing'
    return (
      <div className="space-y-4">
        {isStillParsing ? (
          <ProcessingProgress doc={doc} />
        ) : doc.status === 'error' ? (
          <EmptyState
            title="Field extraction failed"
            description="The document could not be parsed. Try retrying or re-uploading a clearer version."
          />
        ) : (
          <EmptyState
            title="No fields detected"
            description="The document was processed but no form fields were found. If this looks wrong, try re-uploading a clearer copy."
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Sticky status + bulk-action bar so the Save / Apply-all CTAs are
          always reachable on tall field lists, even on a phone. */}
      <div
        ref={stickyBarRef}
        className="sticky top-0 z-10 -mx-4 flex flex-wrap items-center justify-between gap-2 border-b border-border bg-background/95 px-4 py-2 backdrop-blur md:-mx-5 md:px-5"
        data-testid="fields-action-bar"
      >
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/90">
            {doc.fields.length} field{doc.fields.length === 1 ? '' : 's'}
          </span>
          {aiFilledCount > 0 && (
            <Badge tone="success" size="xs">
              <Sparkles aria-hidden />
              {aiFilledCount} AI-filled
            </Badge>
          )}
          {hasChanges && (
            <Badge tone="warning" size="xs">
              {dirty.size} unsaved
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/*
            Hide the mutation buttons entirely for read-only callers — a
            disabled Save would still tempt the user to click it, and the
            top-of-page banner already explains why edits aren't possible.
            The AI-edit bulk button has its own ``aiEditReadOnly`` gate for
            insufficient-role callers; the outer ``!readOnly`` guard hides
            it for the full-document-readonly case (e.g. share-link viewer).
          */}
          {!readOnly && (
            <AiEditBulkButton
              documentId={doc.uuid}
              emptyFieldCount={emptyFieldCount}
              readOnly={aiEditReadOnly}
            />
          )}
          {!readOnly && pendingSuggestions.length > 0 && (
            <Button
              size="sm"
              variant="soft"
              onClick={onApplyAllSuggestions}
              disabled={applyAllSuggestions.isPending}
              data-testid="apply-all-suggestions"
            >
              <Sparkles />
              Apply all ({pendingSuggestions.length})
            </Button>
          )}
          {!readOnly && (
            <Button
              size="sm"
              onClick={onSave}
              disabled={!hasChanges || bulkPatch.isPending}
              data-testid="fields-save-changes"
            >
              Save changes
            </Button>
          )}
        </div>
      </div>

      {aiFilledCount > 0 && (
        <Card
          tone="muted"
          className="border border-success-soft bg-success-soft/60 p-3 text-xs text-success-soft-foreground"
          data-testid="ai-filled-banner"
          data-state="filled"
        >
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <p className="leading-relaxed">
              {aiFilledCount} field{aiFilledCount === 1 ? ' was' : 's were'}{' '}
              auto-filled by the agent. Edit any value below to override — the
              audit log keeps the full history.
            </p>
          </div>
        </Card>
      )}

      {pendingSuggestions.length > 0 && (
        <AiSuggestionsTray doc={doc} />
      )}

      {groups.map(({ label, fields }) => (
        <div key={label} className="space-y-3">
          <div className="aurora-eyebrow">{label}</div>
          <div className="space-y-3">
            {fields.map((field) => (
              <FieldEditor
                key={field.uuid}
                doc={doc}
                field={field}
                value={valueFor(field)}
                isDirty={dirty.has(field.uuid)}
                onChange={(v) => setDirtyValue(field.uuid, v)}
                readOnly={readOnly}
                aiEditReadOnly={aiEditReadOnly}
                onConflict={onConflict}
              />
            ))}
          </div>
        </div>
      ))}

      <OverwriteConflictDialog
        conflict={conflict?.body ?? null}
        isSaving={bulkPatch.isPending}
        onConfirm={onConfirmOverwrite}
        onCancel={onCancelOverwrite}
      />
      <FieldConflictDialog
        documentId={documentId}
        conflict={activeConflict?.conflict ?? null}
        fieldLabel={activeConflict?.fieldLabel ?? ''}
        originalBody={activeConflict?.originalBody}
        onResolved={() => setActiveConflict(null)}
        onDismiss={() => setActiveConflict(null)}
      />
    </div>
  )
}

/**
 * Review tray for AI-proposed field changes that haven't been accepted
 * yet. Distinct from the inline per-field Apply/Dismiss controls below
 * because this surface:
 *  - shows the before / after diff up front
 *  - runs a conflict check against the cached `current_value` before
 *    issuing a write, so a stale suggestion can't silently clobber a
 *    fresh user edit
 *  - exposes an explicit "Apply anyway" path (wired to
 *    `handlers.onForceApply`) for users who confirm the conflict and
 *    want the suggestion to land regardless
 *
 * The inline FieldEditor controls still exist for the simple
 * non-conflict case — both paths converge on the same PATCH endpoint.
 *
 * Exported so the suggestions-path tests can mount it in isolation
 * without booting the whole route.
 */
export function AiSuggestionsTray({ doc }: { doc: DocumentDetail }) {
  const handlers = useFieldEditHandlers(doc.uuid)
  const actions = useMemo(() => fieldsWithPendingSuggestions(doc), [doc])
  if (actions.length === 0) return null
  return (
    <div data-testid="ai-suggestions-tray-wrapper">
      <AiActionTray
        actions={actions}
        onApply={handlers.onApply}
        onReject={handlers.onReject}
        onForceApply={handlers.onForceApply}
        title="Pending AI edits"
        description="Inspect the before / after on each suggestion. Apply individually or batch-select to apply several at once."
      />
    </div>
  )
}

function groupFieldsByPage(
  fields: Field[],
): { label: string; fields: Field[] }[] {
  const map = new Map<string, Field[]>()
  for (const f of fields) {
    const page = (f.anchor as { page?: number } | null | undefined)?.page
    const key = page == null ? 'Document' : `Page ${page}`
    const arr = map.get(key) ?? []
    arr.push(f)
    map.set(key, arr)
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => {
      if (a === 'Document') return 1
      if (b === 'Document') return -1
      return a.localeCompare(b, undefined, { numeric: true })
    })
    .map(([label, fields]) => ({ label, fields }))
}

interface FieldEditorProps {
  doc: DocumentDetail
  field: Field
  value: string | null
  isDirty: boolean
  onChange: (value: string | null) => void
  readOnly?: boolean
  aiEditReadOnly: boolean
  onConflict: (conflict: FieldConflictPayload, originalBody?: PatchSingleFieldBody) => void
}

function FieldEditor({
  doc,
  field,
  value,
  isDirty,
  onChange,
  onConflict,
  readOnly = false,
  aiEditReadOnly,
}: FieldEditorProps) {
  const labelId = `field-${field.uuid}`
  const suggested = field.suggested_value
  const hasPendingSuggestion =
    suggested != null && suggested !== '' && suggested !== field.current_value
  const isAiFilled = field.accepted_by === 'agent' && !isDirty
  const singlePatch = usePatchSingleField(doc.uuid)

  const applySuggestion = async () => {
    if (readOnly || !suggested) return
    const body: PatchSingleFieldBody = {
      current_value: suggested,
      accepted_by: 'agent',
      clear_suggested: true,
    }
    try {
      await singlePatch.mutateAsync({ fieldId: field.uuid, body })
      toast.success(`Applied suggestion for "${fieldDisplayName(field)}"`)
    } catch (e) {
      const conflict = extractFieldConflict(e)
      if (conflict) {
        // Forward the attempted body so "Keep mine" can replay
        // accepted_by='agent' + clear_suggested=true instead of degrading
        // the retry to a bare current_value write.
        onConflict(conflict, body)
        return
      }
      toast.error(e instanceof Error ? e.message : 'Apply failed')
    }
  }

  const dismissSuggestion = async () => {
    if (readOnly) return
    try {
      await singlePatch.mutateAsync({
        fieldId: field.uuid,
        body: { clear_suggested: true },
      })
      toast.success(`Dismissed suggestion for "${fieldDisplayName(field)}"`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Dismiss failed')
    }
  }

  return (
    <div
      data-field-uuid={field.uuid}
      className={cn(
        'space-y-1.5 rounded-md transition-colors',
        hasPendingSuggestion &&
          'border border-warning-soft bg-warning-soft/60 p-2.5',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        {renderFieldLabel(field, labelId)}
        <div className="flex items-center gap-1">
          {hasPendingSuggestion ? (
            <Badge
              tone="warning"
              size="xs"
              data-testid="proposed-marker"
            >
              <Sparkles aria-hidden /> suggested
            </Badge>
          ) : isAiFilled ? (
            <Badge
              tone="success"
              size="xs"
              data-testid="ai-filled-marker"
              title="Auto-filled by the agent. Edit to override."
            >
              <Sparkles aria-hidden /> AI-filled
            </Badge>
          ) : null}
          <AiEditFieldButton
            documentId={doc.uuid}
            field={field}
            readOnly={aiEditReadOnly}
          />
        </div>
      </div>
      {renderFieldControl(field, value, onChange, labelId, readOnly)}
      {hasPendingSuggestion && !readOnly && (
        <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
          <span className="text-warning-soft-foreground/90">
            Suggestion:{' '}
            <span className="font-mono font-medium">{suggested}</span>
          </span>
          <div className="ml-auto flex gap-1">
            <Button
              size="sm"
              variant="default"
              className="h-7 px-2.5"
              onClick={applySuggestion}
              disabled={singlePatch.isPending}
              data-testid={`accept-${field.uuid}`}
            >
              Apply
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2.5"
              onClick={dismissSuggestion}
              disabled={singlePatch.isPending}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
      {hasPendingSuggestion && readOnly && (
        <div
          className="flex flex-wrap items-center gap-2 pt-1 text-xs"
          data-testid={`field-suggestion-readonly-${field.uuid}`}
        >
          <span className="text-warning-soft-foreground/90">
            Suggestion:{' '}
            <span className="font-mono font-medium">{suggested}</span>
          </span>
        </div>
      )}
    </div>
  )
}

function renderFieldLabel(field: Field, labelId: string) {
  const display = fieldDisplayName(field)
  const showTooltip = display !== field.name
  const label = (
    <Label
      htmlFor={labelId}
      className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/80"
      title={field.name}
    >
      {display}
    </Label>
  )
  if (!showTooltip) return label
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{label}</TooltipTrigger>
        <TooltipContent data-testid="field-raw-name-tooltip">
          <span className="font-mono text-xs">{field.name}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function renderFieldControl(
  field: Field,
  value: string | null,
  onChange: (value: string | null) => void,
  id: string,
  readOnly = false,
) {
  switch (field.kind) {
    case 'checkbox': {
      const checked = isCheckboxChecked(value)
      return (
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
            disabled={readOnly}
            className="size-4 rounded border-input accent-primary"
          />
          <span className="text-muted-foreground">
            {checked ? 'Checked' : 'Unchecked'}
          </span>
        </label>
      )
    }
    case 'date':
      return (
        <Input
          id={id}
          type="date"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={readOnly}
        />
      )
    case 'signature':
      return (
        <div className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 text-sm italic text-muted-foreground">
          {value || 'Click to sign (not implemented)'}
        </div>
      )
    case 'select':
      return (
        <select
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={readOnly}
          className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-soft outline-none transition-[box-shadow,border-color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="">—</option>
          {(field.suggested_value ? [field.suggested_value] : [])
            .concat(value && value !== field.suggested_value ? [value] : [])
            .map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
        </select>
      )
    case 'text':
    default:
      return (
        <Input
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={field.suggested_value ?? ''}
          disabled={readOnly}
        />
      )
  }
}

// ─────────────────────────── Download dialog ─────────────────────────────

function DownloadDialog({ doc }: { doc: DocumentDetail }) {
  const defaultFormat: OutputFormat =
    doc.mime_type === MIME_DOCX ? 'docx' : 'pdf'
  const [open, setOpen] = useState(false)
  const [format, setFormat] = useState<OutputFormat>(defaultFormat)
  const render = useRenderDocument(doc.uuid)

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) {
      setFormat(defaultFormat)
      render.reset()
    }
  }

  const onSubmit = async () => {
    try {
      const data = await render.mutateAsync({
        format,
        include_suggestions: false,
      })
      const output = data.outputs[0]
      if (output) {
        window.open(output.download_url, '_blank', 'noopener,noreferrer')
      }
      setOpen(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Render failed')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="size-9 px-0 md:size-auto md:px-3.5"
          data-testid="download-trigger"
        >
          <Download />
          <span className="hidden sm:inline">Download</span>
          <span className="sr-only sm:hidden">Download document</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Download filled document</DialogTitle>
          <DialogDescription>
            Render the document with current field values and open it in a new
            tab.
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-2" disabled={render.isPending}>
          <legend className="text-sm font-medium">Format</legend>
          <FormatOption
            label="PDF"
            value="pdf"
            checked={format === 'pdf'}
            onChange={setFormat}
          />
          <FormatOption
            label="DOCX"
            value="docx"
            checked={format === 'docx'}
            onChange={setFormat}
          />
        </fieldset>

        {render.error && (
          <p className="text-sm text-destructive" role="alert">
            {(render.error as Error).message}
          </p>
        )}

        <DialogFooter>
          <Button
            onClick={onSubmit}
            disabled={render.isPending}
            data-testid="download-submit"
          >
            {render.isPending && <Loader2 className="animate-spin" />}
            Render &amp; download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function FormatOption({
  label,
  value,
  checked,
  onChange,
}: {
  label: string
  value: OutputFormat
  checked: boolean
  onChange: (value: OutputFormat) => void
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2.5 rounded-md border border-border bg-card px-3 py-2 text-sm transition-colors',
        checked && 'border-primary/60 bg-primary-soft/60',
      )}
    >
      <input
        type="radio"
        name="download-format"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="size-4 accent-primary"
      />
      {label}
      {checked && (
        <CheckCircle2 className="ml-auto size-4 text-primary" aria-hidden />
      )}
    </label>
  )
}

// ─────────────────────────── Versions & audit tab ────────────────────────

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function AuditPanel({ doc }: { doc: DocumentDetail }) {
  const [filter, setFilter] = useState<EditActor | 'all'>('all')
  const audit = useDocumentAudit(
    doc.uuid,
    filter === 'all' ? undefined : filter,
  )

  return (
    <div className="space-y-4">
      <div
        className="inline-flex rounded-md border border-border bg-card p-0.5"
        role="group"
        aria-label="Audit filter"
      >
        <FilterChip
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          label="All"
        />
        <FilterChip
          active={filter === 'user'}
          onClick={() => setFilter('user')}
          label="User"
        />
        <FilterChip
          active={filter === 'agent'}
          onClick={() => setFilter('agent')}
          label="Agent"
        />
      </div>

      {audit.isLoading ? (
        <SkeletonList rows={3} rowClassName="h-16" />
      ) : audit.error ? (
        <InlineError
          message={
            audit.error instanceof Error
              ? audit.error.message
              : 'Failed to load audit log.'
          }
        />
      ) : !audit.data || audit.data.length === 0 ? (
        <EmptyState
          title="No edits recorded"
          description="Field changes made by you or the agent will appear here."
          variant="bare"
        />
      ) : (
        <ul className="space-y-1.5">
          {audit.data.map((entry, idx) => (
            <li key={`${entry.field_uuid}-${entry.created_at}-${idx}`}>
              <AuditEntry entry={entry} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-7 items-center rounded-[5px] px-3 text-xs font-medium tracking-tight transition-colors',
        active
          ? 'bg-primary text-primary-foreground shadow-soft'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {label}
    </button>
  )
}

function AuditEntry({ entry }: { entry: FieldEdit }) {
  const isAgent = entry.actor === 'agent'
  return (
    <div className="relative rounded-md border border-border bg-card p-3 pl-4">
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-2 left-0 w-1 rounded-r-sm',
          isAgent ? 'bg-primary' : 'bg-border-strong',
        )}
      />
      <div className="flex items-center gap-2">
        <Badge tone={isAgent ? 'primary' : 'neutral'} size="xs">
          {isAgent ? 'Agent' : 'User'}
        </Badge>
        <span className="truncate text-sm font-medium text-foreground">
          {entry.friendly_name || entry.field_name}
        </span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        <span className="line-through opacity-70">
          {entry.old_value || '(empty)'}
        </span>{' '}
        →{' '}
        <span className="font-medium text-foreground">
          {entry.new_value || '(empty)'}
        </span>
      </p>
      <time
        className="mt-1 block text-[11px] text-muted-foreground"
        dateTime={entry.created_at}
      >
        {formatAuditTime(entry.created_at)}
      </time>
    </div>
  )
}

function formatAuditTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return DATE_FORMAT.format(date)
}

// ─────────────────────────── Versions tab ───────────────────────────────
//
// Renders a newest-first list of saved revisions. Clicking a row opens a
// dialog hydrated by ``useDocumentRevision`` showing the per-field diff
// (was → became → currently) so the user can spot drift between this
// revision and the document's current state.

function VersionsPanel({ doc }: { doc: DocumentDetail }) {
  const revisions = useDocumentRevisions(doc.uuid)
  const [openRevisionUuid, setOpenRevisionUuid] = useState<string | null>(null)

  return (
    <div className="space-y-5">
      <section
        className="space-y-3 rounded-lg border border-border bg-card p-3"
        aria-labelledby="versions-heading"
      >
        <div className="space-y-1">
          <h2 id="versions-heading" className="text-sm font-semibold tracking-tight">
            Versions
          </h2>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Every save, agent fill, and prefill creates a revision. Open one to
            compare it against the document's current values.
          </p>
        </div>

        {revisions.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : revisions.error ? (
          <p className="text-sm text-destructive">
            {revisions.error instanceof Error
              ? revisions.error.message
              : 'Failed to load revisions.'}
          </p>
        ) : !revisions.data || revisions.data.length === 0 ? (
          <EmptyState
            title="No revisions yet"
            description="Save a field change or run agent fill to start the history."
            data-testid="versions-empty"
          />
        ) : (
          <ul className="space-y-2" data-testid="versions-list">
            {revisions.data.map((rev) => (
              <li key={rev.uuid}>
                <RevisionRow
                  revision={rev}
                  onOpen={() => setOpenRevisionUuid(rev.uuid)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section
        className="space-y-3 rounded-lg border border-border bg-card p-3"
        aria-labelledby="audit-heading"
        data-testid="versions-audit-section"
      >
        <div className="space-y-1">
          <h2 id="audit-heading" className="text-sm font-semibold tracking-tight">
            Audit log
          </h2>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Field-level changes grouped by actor, newest first.
          </p>
        </div>
        <AuditPanel doc={doc} />
      </section>

      <RevisionDetailDialog
        documentUuid={doc.uuid}
        revisionUuid={openRevisionUuid}
        onClose={() => setOpenRevisionUuid(null)}
      />
    </div>
  )
}

function actorBadge(actor: RevisionActor): {
  tone: 'primary' | 'neutral' | 'info'
  Icon: typeof UserIcon
  label: string
} {
  switch (actor) {
    case 'agent':
      return { tone: 'primary', Icon: Bot, label: 'Agent' }
    case 'system':
      return { tone: 'info', Icon: Settings2, label: 'System' }
    case 'user':
    default:
      return { tone: 'neutral', Icon: UserIcon, label: 'User' }
  }
}

function RevisionRow({
  revision,
  onOpen,
}: {
  revision: RevisionSummary
  onOpen: () => void
}) {
  const { tone, Icon, label } = actorBadge(revision.actor)
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-border-strong hover:bg-accent/60"
      data-testid="revision-row"
    >
      <span
        aria-hidden
        className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
      >
        <GitCommitHorizontal className="size-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            v{revision.revision_number}
          </span>
          <Badge tone={tone} size="xs">
            <Icon aria-hidden /> {label}
          </Badge>
          <Badge tone="outline" size="xs">
            {revision.change_count} change
            {revision.change_count === 1 ? '' : 's'}
          </Badge>
        </div>
        {revision.summary && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            {revision.summary}
          </p>
        )}
        <time
          className="block text-[11px] text-muted-foreground"
          dateTime={revision.created_at}
        >
          {formatAuditTime(revision.created_at)}
        </time>
      </div>
    </button>
  )
}

function RevisionDetailDialog({
  documentUuid,
  revisionUuid,
  onClose,
}: {
  documentUuid: string
  revisionUuid: string | null
  onClose: () => void
}) {
  const detail = useDocumentRevision(
    documentUuid,
    revisionUuid ?? undefined,
  )
  const revertRevision = usePatchFields(documentUuid)
  const isOpen = revisionUuid !== null
  const canRevert = Boolean(detail.data && detail.data.changes.length > 0)

  const handleRevert = async () => {
    if (!detail.data) return
    try {
      await revertRevision.mutateAsync({
        fields: detail.data.changes.map((change) => ({
          uuid: change.field_uuid,
          current_value: change.old_value ?? null,
        })),
        base_revision_uuid: detail.data.uuid,
      })
      toast.success(`Reverted revision v${detail.data.revision_number}`)
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Revert failed')
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-2xl"
        data-testid="revision-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {detail.data
              ? `Revision v${detail.data.revision_number}`
              : 'Revision'}
          </DialogTitle>
          <DialogDescription>
            {detail.data ? (
              <>
                {actorBadge(detail.data.actor).label} •{' '}
                {formatAuditTime(detail.data.created_at)}
                {detail.data.summary ? ` — ${detail.data.summary}` : ''}
              </>
            ) : (
              'Loading…'
            )}
          </DialogDescription>
        </DialogHeader>

        {detail.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : detail.error ? (
          <p className="text-sm text-destructive">
            {detail.error instanceof Error
              ? detail.error.message
              : 'Failed to load revision.'}
          </p>
        ) : detail.data && detail.data.changes.length > 0 ? (
          <ul className="space-y-2" data-testid="revision-detail-changes">
            {detail.data.changes.map((change) => {
              const drifted =
                change.current_value !== change.new_value
              return (
                <li
                  key={change.field_uuid}
                  className="rounded-md border border-border bg-card p-3"
                  data-testid="revision-detail-change"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-medium text-foreground">
                      {change.friendly_name || change.field_name}
                    </span>
                    {drifted && (
                      <Badge
                        tone="warning"
                        size="xs"
                        data-testid="drift-badge"
                      >
                        Overwritten since
                      </Badge>
                    )}
                  </div>
                  <dl className="mt-1.5 grid grid-cols-3 gap-x-3 text-xs">
                    <DiffCell label="Was" value={change.old_value} muted />
                    <DiffCell
                      label="Became"
                      value={change.new_value}
                      highlight
                    />
                    <DiffCell
                      label="Currently"
                      value={change.current_value}
                      drift={drifted}
                    />
                  </dl>
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            No per-field changes recorded for this revision.
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleRevert}
            disabled={!canRevert || revertRevision.isPending}
            data-testid="revision-revert"
          >
            {revertRevision.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <RotateCcw className="size-4" aria-hidden />
            )}
            Revert changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function DiffCell({
  label,
  value,
  muted = false,
  highlight = false,
  drift = false,
}: {
  label: string
  value: string | null | undefined
  muted?: boolean
  highlight?: boolean
  drift?: boolean
}) {
  return (
    <div className="min-w-0 space-y-0.5">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          'break-words font-mono',
          muted && 'text-muted-foreground',
          highlight && 'font-medium text-foreground',
          drift && 'text-warning-soft-foreground',
        )}
      >
        {value == null || value === '' ? (
          <span className="italic text-muted-foreground">(empty)</span>
        ) : (
          value
        )}
      </dd>
    </div>
  )
}

// ─────────────────────────── Overwrite dialog ───────────────────────────
//
// Rendered by FieldsPanel when a Save returns 409. Shows the conflicting
// fields' base/current/incoming values so the user can decide whether to
// force-overwrite the agent's concurrent edits or back out and reconcile.

function OverwriteConflictDialog({
  conflict,
  isSaving,
  onConfirm,
  onCancel,
}: {
  conflict: RevisionConflict | null
  isSaving: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const open = conflict !== null
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-2xl"
        data-testid="overwrite-conflict-dialog"
      >
        <DialogHeader>
          <DialogTitle>Overwrite concurrent changes?</DialogTitle>
          <DialogDescription>
            {conflict ? (
              <>
                The document changed since you opened it
                {conflict.current_revision.actor === 'agent'
                  ? ' (the agent wrote new values)'
                  : ''}
                . Your save would replace{' '}
                {conflict.conflicting_fields.length} field
                {conflict.conflicting_fields.length === 1 ? '' : 's'}.
              </>
            ) : (
              ''
            )}
          </DialogDescription>
        </DialogHeader>

        {conflict && (
          <ul
            className="space-y-2"
            data-testid="overwrite-conflict-fields"
          >
            {conflict.conflicting_fields.map((field) => (
              <li
                key={field.field_uuid}
                className="rounded-md border border-warning-soft bg-warning-soft/50 p-3"
              >
                <div className="text-sm font-medium text-foreground">
                  {field.friendly_name || field.field_name}
                </div>
                <dl className="mt-1.5 grid grid-cols-3 gap-x-3 text-xs">
                  <DiffCell label="Was" value={field.base_value} muted />
                  <DiffCell
                    label="Currently"
                    value={field.current_value}
                    drift
                  />
                  <DiffCell
                    label="Your edit"
                    value={field.incoming_value}
                    highlight
                  />
                </dl>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
            data-testid="overwrite-cancel"
          >
            Keep current values
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isSaving}
            data-testid="overwrite-confirm"
          >
            {isSaving && <Loader2 className="animate-spin" />}
            Overwrite anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────── Sharing tab ─────────────────────────────────

const SHARE_ROLES: WorkspaceRole[] = ['viewer', 'editor', 'admin']

function roleTone(
  role: WorkspaceRole,
): 'neutral' | 'primary' | 'success' | 'warning' {
  switch (role) {
    case 'owner':
      return 'warning'
    case 'admin':
      return 'primary'
    case 'editor':
      return 'success'
    default:
      return 'neutral'
  }
}

function workspaceMemberLabel(member: WorkspaceMemberRead): string {
  return member.name?.trim() || member.email
}

function shareLinkLabel(link: DocumentShareLinkRead, party?: DocumentPartyRead) {
  if (link.scope === 'party') {
    return `${party?.name || party?.email || 'Recipient'} signing link`
  }
  return link.role === 'editor' ? 'Public edit link' : 'Public view link'
}

function shareFlowLinkFromLink(
  link: DocumentShareLinkRead,
  party?: DocumentPartyRead,
): ShareFlowLink | null {
  if (!link.public_url || link.revoked_at) return null
  return {
    id: link.uuid,
    label: shareLinkLabel(link, party),
    url: link.public_url,
    mode: link.role === 'editor' ? 'edit' : 'view',
  }
}

function partyStatusFromEvents(
  party: DocumentPartyRead,
  links: readonly DocumentShareLinkRead[],
  events: readonly DocumentAccessEventRead[],
): Pick<ShareFlowParty, 'status' | 'progressPercent' | 'viewedAt' | 'editedAt'> {
  const partyLinks = links.filter((link) => link.party_uuid === party.uuid)
  const linkUuids = new Set(partyLinks.map((link) => link.uuid))
  const partyEvents = events.filter(
    (event) =>
      event.party_uuid === party.uuid ||
      (event.share_link_uuid ? linkUuids.has(event.share_link_uuid) : false),
  )
  const editedAt =
    partyEvents.find(
      (event) =>
        event.event_type === 'edit' ||
        event.event_type === 'sign' ||
        event.event_type === 'comment',
    )?.created_at ?? null
  const viewedAt =
    partyEvents.find(
      (event) =>
        event.event_type === 'view' ||
        event.event_type === 'email_viewed' ||
        event.event_type === 'email_clicked',
    )?.created_at ??
    partyLinks.find((link) => link.last_accessed_at)?.last_accessed_at ??
    null
  const status: PartyStatus = editedAt ? 'in_progress' : viewedAt ? 'viewed' : 'not_started'
  return {
    status,
    progressPercent: status === 'in_progress' ? 66 : status === 'viewed' ? 33 : 0,
    viewedAt,
    editedAt,
  }
}

function buildPartyProgress(
  parties: readonly DocumentPartyRead[],
  links: readonly DocumentShareLinkRead[],
  events: readonly DocumentAccessEventRead[],
): ShareFlowParty[] {
  return parties.map((party) => {
    const partyLinks = links.filter((link) => link.party_uuid === party.uuid && !link.revoked_at)
    const viewLink = partyLinks.find((link) => link.role === 'viewer')
    const editLink = partyLinks.find((link) => link.role !== 'viewer')
    const status = partyStatusFromEvents(party, links, events)
    return {
      id: party.uuid,
      name: party.name || party.email || (party.is_owner_sender ? 'Sender' : 'Recipient'),
      email: party.email,
      role: party.role,
      dueAt: party.due_at ?? null,
      ...status,
      viewUrl: viewLink?.public_url ?? null,
      editUrl: editLink?.public_url ?? null,
    }
  })
}

function trackingFromParties(parties: readonly ShareFlowParty[]) {
  return {
    viewedCount: parties.filter((party) => party.status !== 'not_started').length,
    editedCount: parties.filter(
      (party) => party.status === 'in_progress' || party.status === 'completed',
    ).length,
    totalParties: parties.length,
  }
}

export function SharingPanel({
  doc,
  documentRole,
}: {
  doc: DocumentDetail
  documentRole: WorkspaceRole | null
}) {
  const shares = useDocumentShares(doc.uuid)
  const createShare = useCreateDocumentShare(doc.uuid)
  const revokeShare = useRevokeDocumentShare(doc.uuid)
  const members = useWorkspaceMembers(doc.workspace_uuid ?? undefined)
  const parties = useDocumentParties(doc.uuid)
  const createParty = useCreateDocumentParty(doc.uuid)
  const shareLinks = useDocumentShareLinks(doc.uuid)
  const createShareLink = useCreateDocumentShareLink(doc.uuid)
  const revokeShareLink = useRevokeDocumentShareLink(doc.uuid)
  const tracking = useDocumentTracking(doc.uuid)

  const [memberQuery, setMemberQuery] = useState('')
  const [selectedMember, setSelectedMember] = useState<WorkspaceMemberRead | null>(null)
  const [role, setRole] = useState<WorkspaceRole>('viewer')
  const [submitting, setSubmitting] = useState(false)
  const [partyName, setPartyName] = useState('')
  const [partyEmail, setPartyEmail] = useState('')
  const [partyRole, setPartyRole] = useState<DocumentPartyRole>('signer')
  const [partyDueAt, setPartyDueAt] = useState('')

  const canManageShareLinks = documentRole !== null && roleSatisfies(documentRole, 'admin')
  const canEditParties = documentRole !== null && roleSatisfies(documentRole, 'editor')
  const partyByUuid = useMemo(
    () => new Map((parties.data ?? []).map((party) => [party.uuid, party])),
    [parties.data],
  )
  const publicLinkRecords = useMemo(
    () => (shareLinks.data ?? []).filter((link) => link.scope === 'public' && !link.revoked_at),
    [shareLinks.data],
  )
  const participantLinkRecords = useMemo(
    () => (shareLinks.data ?? []).filter((link) => link.scope === 'party' && !link.revoked_at),
    [shareLinks.data],
  )
  const publicLinks = useMemo(
    () =>
      publicLinkRecords
        .map((link) => shareFlowLinkFromLink(link))
        .filter((link): link is ShareFlowLink => link !== null),
    [publicLinkRecords],
  )
  const participantLinks = useMemo(
    () =>
      participantLinkRecords
        .map((link) => shareFlowLinkFromLink(link, partyByUuid.get(link.party_uuid ?? '')))
        .filter((link): link is ShareFlowLink => link !== null),
    [participantLinkRecords, partyByUuid],
  )
  const participantProgress = useMemo(
    () => buildPartyProgress(parties.data ?? [], shareLinks.data ?? [], tracking.data ?? []),
    [parties.data, shareLinks.data, tracking.data],
  )
  const trackingSummary = useMemo(
    () => trackingFromParties(participantProgress),
    [participantProgress],
  )
  const memberMatches = useMemo(() => {
    const query = memberQuery.trim().toLowerCase()
    const candidates = (members.data ?? []).filter((member) => !member.is_self)
    if (!query) return candidates.slice(0, 6)
    return candidates
      .filter((member) => {
        const haystack = `${member.name ?? ''} ${member.email}`.toLowerCase()
        return haystack.includes(query)
      })
      .slice(0, 6)
  }, [memberQuery, members.data])

  const onShare = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedMember || submitting) return
    setSubmitting(true)
    try {
      await createShare.mutateAsync({ user_uuid: selectedMember.user_uuid, role })
      toast.success(`Shared with ${selectedMember.email}`)
      setMemberQuery('')
      setSelectedMember(null)
      setRole('viewer')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Share failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onRevoke = async (share: DocumentShareRead) => {
    if (!confirm(`Revoke ${share.user_email}'s access to this document?`)) return
    try {
      await revokeShare.mutateAsync(share.uuid)
      toast.success(`Revoked access for ${share.user_email}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Revoke failed')
    }
  }

  const onCreatePublicLink = async (roleForLink: WorkspaceRole) => {
    try {
      const link = await createShareLink.mutateAsync({
        scope: 'public',
        role: roleForLink,
      })
      if (link.public_url) {
        await navigator.clipboard.writeText(link.public_url)
        toast.success(`${roleForLink === 'editor' ? 'Edit' : 'View'} link created and copied`)
      } else {
        toast.success('Link created')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create share link')
    }
  }

  const onAddSigningRecipient = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedName = partyName.trim()
    const trimmedEmail = partyEmail.trim()
    if (!trimmedEmail || createParty.isPending || createShareLink.isPending) return
    const dueAtIso = partyDueAt ? new Date(partyDueAt).toISOString() : null
    try {
      const party = await createParty.mutateAsync({
        email: trimmedEmail,
        name: trimmedName,
        role: partyRole,
        is_owner_sender: false,
        due_at: dueAtIso,
      })
      const link = await createShareLink.mutateAsync({
        scope: 'party',
        party_uuid: party.uuid,
        role: partyRole === 'viewer' ? 'viewer' : 'editor',
        expires_at: dueAtIso,
      })
      if (link.public_url) {
        await navigator.clipboard.writeText(link.public_url)
        toast.success(`Recipient link for ${trimmedEmail} created and copied`)
      } else {
        toast.success(`Recipient ${trimmedEmail} added`)
      }
      setPartyName('')
      setPartyEmail('')
      setPartyRole('signer')
      setPartyDueAt('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add recipient')
    }
  }

  const onRevokeLink = async (linkUuid: string) => {
    if (!confirm('Revoke this share link? Anyone with the URL will lose access.')) return
    try {
      await revokeShareLink.mutateAsync(linkUuid)
      toast.success('Share link revoked')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not revoke link')
    }
  }

  const onCopy = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success(`${label} copied`)
    } catch {
      toast.error('Could not copy link')
    }
  }

  return (
    <div className="space-y-5">
      {/* Inherited access callout — explains how workspace membership
          already grants access before any direct share is listed. The UI
          can't query the parent workspace's members here (no field on
          DocumentDetail), so we surface the rule rather than the names. */}
      <div
        className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground"
        data-testid="inherited-access"
      >
        <Info className="mt-0.5 size-4 shrink-0 text-info" aria-hidden />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Inherited workspace access
          </p>
          <p>
            Members of this document&apos;s workspace already have access at
            their workspace role (viewer / editor / admin / owner). The
            entries below are <strong>direct shares</strong> that grant
            access to people outside the workspace, or upgrade a member to a
            higher role on this document specifically.
          </p>
        </div>
      </div>

      <ShareLinkSection
        links={publicLinks}
        activeLinkCount={publicLinkRecords.length}
        isLoading={shareLinks.isLoading}
        canCreate={canManageShareLinks}
        isCreating={createShareLink.isPending}
        onCreatePublicLink={onCreatePublicLink}
        onRevokeLink={onRevokeLink}
        onCopy={onCopy}
      />

      <PartyProgressSection
        parties={participantProgress}
        tracking={trackingSummary}
        links={participantLinks}
        activeLinkCount={participantLinkRecords.length}
        isLoading={parties.isLoading || shareLinks.isLoading || tracking.isLoading}
        canRevokeLinks={canManageShareLinks}
        onRevokeLink={onRevokeLink}
        onCopy={onCopy}
      />

      {canEditParties ? (
        <form
          onSubmit={onAddSigningRecipient}
          className="space-y-3 rounded-md border border-border bg-card p-3"
          data-testid="signing-recipient-form"
        >
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              Add recipient
            </p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Add the person who needs to view, fill, edit, or sign assigned fields. The
              app creates their private recipient link immediately and copies it
              to your clipboard.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,_1fr)_minmax(0,_1fr)_minmax(12rem,_0.8fr)_auto]">
            <Input
              type="text"
              placeholder="Recipient name"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              disabled={createParty.isPending || createShareLink.isPending}
              data-testid="party-name-input"
            />
            <Input
              type="email"
              placeholder="recipient@example.com"
              value={partyEmail}
              onChange={(e) => setPartyEmail(e.target.value)}
              required
              disabled={createParty.isPending || createShareLink.isPending}
              data-testid="party-email-input"
            />
            <Input
              type="datetime-local"
              value={partyDueAt}
              onChange={(e) => setPartyDueAt(e.target.value)}
              disabled={createParty.isPending || createShareLink.isPending}
              aria-label="Optional signing deadline"
              data-testid="party-due-at-input"
            />
            <select
              value={partyRole}
              onChange={(e) => setPartyRole(e.target.value as DocumentPartyRole)}
              disabled={createParty.isPending || createShareLink.isPending}
              className="h-10 rounded-md border border-input bg-card px-3 text-sm shadow-soft outline-none transition-[box-shadow,border-color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30"
              data-testid="party-role-select"
            >
              <option value="signer">Signer</option>
              <option value="filler">Filler</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={!partyEmail.trim() || createParty.isPending || createShareLink.isPending}
            data-testid="party-submit"
          >
            {(createParty.isPending || createShareLink.isPending) && (
              <Loader2 className="animate-spin" />
            )}
            Add recipient
          </Button>
        </form>
      ) : (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          You need editor access to add recipients.
        </p>
      )}

      {/* Current shares */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">
          Direct shares
        </p>

        {shares.isLoading ? (
          <SkeletonList rows={2} rowClassName="h-12" />
        ) : shares.error ? (
          <InlineError
            message={
              shares.error instanceof Error
                ? shares.error.message
                : 'Failed to load shares.'
            }
          />
        ) : !shares.data || shares.data.length === 0 ? (
          <EmptyState
            title="No workspace-user shares yet"
            description="Use this only when an existing DocFiller user needs authenticated dashboard access. For recipient signing or filling, add a recipient above."
          />
        ) : (
          <ul className="space-y-2" data-testid="shares-list">
            {shares.data.map((share) => (
              <li
                key={share.uuid}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2"
                data-testid={`share-row-${share.uuid}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {share.user_name || share.user_email}
                  </p>
                  {share.user_name && (
                    <p className="truncate text-xs text-muted-foreground">
                      {share.user_email}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={roleTone(share.role)} size="xs">
                    {share.role}
                  </Badge>
                  <Badge tone="outline" size="xs">
                    Direct
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => onRevoke(share)}
                    disabled={revokeShare.isPending}
                    data-testid={`revoke-${share.uuid}`}
                  >
                    Revoke
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Share form */}
      <form onSubmit={onShare} className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground">
          Grant dashboard access to a DocFiller user
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="min-w-0 flex-1 space-y-1.5">
            <Input
              type="text"
              placeholder="Search workspace members"
              value={memberQuery}
              onChange={(e) => {
                setMemberQuery(e.target.value)
                setSelectedMember(null)
              }}
              disabled={submitting || !doc.workspace_uuid}
              className="flex-1"
              data-testid="share-member-input"
              aria-label="Search workspace members"
            />
            <div
              className="max-h-44 overflow-y-auto rounded-md border border-border bg-card"
              data-testid="share-member-suggestions"
            >
              {!doc.workspace_uuid ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  This document is not in a workspace.
                </p>
              ) : members.isLoading ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  Loading workspace members...
                </p>
              ) : members.error ? (
                <p className="px-3 py-2 text-xs text-destructive">
                  Could not load workspace members.
                </p>
              ) : memberMatches.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  {memberQuery.trim()
                    ? 'No workspace members match that search.'
                    : 'No other workspace members found.'}
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {memberMatches.map((member) => {
                    const selected = selectedMember?.user_uuid === member.user_uuid
                    return (
                      <li key={member.user_uuid}>
                        <button
                          type="button"
                          className={cn(
                            'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted/60',
                            selected && 'bg-muted',
                          )}
                          onClick={() => {
                            setSelectedMember(member)
                            setMemberQuery(`${workspaceMemberLabel(member)} <${member.email}>`)
                          }}
                          data-testid={`share-member-option-${member.user_uuid}`}
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-foreground">
                              {workspaceMemberLabel(member)}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {member.email}
                            </span>
                          </span>
                          <Badge tone={roleTone(member.role)} size="xs">
                            {member.role}
                          </Badge>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as WorkspaceRole)}
            disabled={submitting}
            className="h-10 shrink-0 rounded-md border border-input bg-card px-3 text-sm shadow-soft outline-none transition-[box-shadow,border-color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/30"
            data-testid="share-role-select"
          >
            {SHARE_ROLES.map((r) => (
              <option key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <Button
          type="submit"
          size="sm"
          disabled={submitting || !selectedMember}
          data-testid="share-submit"
        >
          {submitting && <Loader2 className="animate-spin" />}
          Share
        </Button>
      </form>
    </div>
  )
}

function ShareLinkSection({
  links,
  activeLinkCount,
  isLoading,
  canCreate,
  isCreating,
  onCreatePublicLink,
  onRevokeLink,
  onCopy,
}: {
  links: ShareFlowLink[]
  activeLinkCount: number
  isLoading: boolean
  canCreate: boolean
  isCreating: boolean
  onCreatePublicLink: (role: WorkspaceRole) => void
  onRevokeLink: (linkUuid: string) => void
  onCopy: (url: string, label: string) => void
}) {
  return (
    <div className="space-y-2" data-testid="public-share-links">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-muted-foreground">
          Public links
        </p>
        <Badge tone={links.length > 0 ? 'success' : 'outline'} size="xs">
          {links.length > 0 ? 'Shareable' : 'Not enabled'}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onCreatePublicLink('viewer')}
          disabled={!canCreate || isCreating}
          data-testid="create-public-view-link"
        >
          {isCreating ? <Loader2 className="animate-spin" /> : <Eye />}
          Create view link
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onCreatePublicLink('editor')}
          disabled={!canCreate || isCreating}
          data-testid="create-public-edit-link"
        >
          {isCreating ? <Loader2 className="animate-spin" /> : <Settings2 />}
          Create edit link
        </Button>
      </div>
      {!canCreate && (
        <p className="text-xs text-muted-foreground">
          You need admin access to create public links.
        </p>
      )}
      {isLoading ? (
        <SkeletonList rows={1} rowClassName="h-16" />
      ) : links.length === 0 ? (
        <p
          className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
          data-testid="public-share-links-empty"
        >
          {activeLinkCount > 0
            ? 'Public links are active, but DocFiller only reveals the URL when a link is created. Create a new link if you need to copy one now.'
            : 'No public links have been created. Use the buttons above to create a view-only or editable link.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {links.map((link) => (
            <li
              key={link.id}
              className="rounded-md border border-border bg-card p-3"
              data-testid={`public-share-link-${link.mode}`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    {link.mode === 'edit' ? (
                      <Settings2 className="size-4 text-success" aria-hidden />
                    ) : (
                      <Eye className="size-4 text-info" aria-hidden />
                    )}
                    {link.label}
                  </p>
                  <p className="truncate font-mono text-xs text-muted-foreground">
                    {link.url}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onCopy(link.url, link.label)}
                    data-testid={`copy-${link.id}`}
                  >
                    <Copy />
                    Copy
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => window.open(link.url, '_blank', 'noreferrer')}
                  >
                    <ExternalLink />
                    Open
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onRevokeLink(link.id)}
                    disabled={!canCreate}
                    data-testid={`revoke-link-${link.id}`}
                  >
                    Revoke
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function PartyProgressSection({
  parties,
  tracking,
  links,
  activeLinkCount,
  isLoading,
  canRevokeLinks,
  onRevokeLink,
  onCopy,
}: {
  parties: ShareFlowParty[]
  tracking: {
    viewedCount: number
    editedCount: number
    totalParties: number
  }
  links: ShareFlowLink[]
  activeLinkCount: number
  isLoading: boolean
  canRevokeLinks: boolean
  onRevokeLink: (linkUuid: string) => void
  onCopy: (url: string, label: string) => void
}) {
  return (
    <div className="space-y-3" data-testid="party-progress">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-muted-foreground">
          Participant progress
        </p>
        <Badge tone="outline" size="xs">
          {tracking.viewedCount}/{tracking.totalParties} started
        </Badge>
      </div>

      <div className="grid gap-2 sm:grid-cols-2" data-testid="tracking-summary">
        <TrackingMetric label="Viewed" value={tracking.viewedCount} />
        <TrackingMetric label="Edited" value={tracking.editedCount} />
      </div>

      {isLoading ? (
        <SkeletonList rows={2} rowClassName="h-20" />
      ) : parties.length === 0 ? (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          No recipients yet. Add a recipient below to create a private
          link and start tracking views, fills, signatures, and comments.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="party-progress-list">
          {parties.map((party) => (
            <li
              key={party.id}
              className="rounded-md border border-border bg-card p-3"
              data-testid={`party-progress-${party.id}`}
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {party.name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {party.email ?? 'Recipient identity pending'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {party.role && (
                      <Badge tone="outline" size="xs">
                        {party.role}
                      </Badge>
                    )}
                    <Badge tone={partyStatusTone(party.status)} size="xs">
                      {partyStatusLabel(party.status)}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Progress</span>
                    <span>{party.progressPercent}%</span>
                  </div>
                  <Progress percent={party.progressPercent} />
                </div>

                <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                  <span>
                    Viewed:{' '}
                    {party.viewedAt ? formatLocalDateTime(party.viewedAt) : 'Not yet'}
                  </span>
                  <span>
                    Edited:{' '}
                    {party.editedAt ? formatLocalDateTime(party.editedAt) : 'Not yet'}
                  </span>
                  <span className="sm:col-span-2">
                    Due:{' '}
                    {party.dueAt ? formatLocalDateTime(party.dueAt) : 'No deadline'}
                  </span>
                </div>

                {(party.viewUrl || party.editUrl) && (
                  <div className="flex flex-wrap gap-2">
                    {party.viewUrl && (
                      <PartyLinkButton
                        label="View link"
                        url={party.viewUrl}
                        onCopy={onCopy}
                      />
                    )}
                    {party.editUrl && (
                      <PartyLinkButton
                        label="Edit link"
                        url={party.editUrl}
                        onCopy={onCopy}
                      />
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {links.length > 0 && (
        <div className="space-y-2" data-testid="party-share-links">
          <p className="text-xs font-medium text-muted-foreground">
            Active recipient links
          </p>
          <ul className="space-y-2">
            {links.map((link) => (
              <li
                key={link.id}
                className="flex flex-col gap-2 rounded-md border border-border bg-card p-3 sm:flex-row sm:items-center"
                data-testid={`party-share-link-${link.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {link.label}
                  </p>
                  <p className="truncate font-mono text-xs text-muted-foreground">
                    {link.url}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onCopy(link.url, link.label)}
                    data-testid={`copy-${link.id}`}
                  >
                    <Copy />
                    Copy
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onRevokeLink(link.id)}
                    disabled={!canRevokeLinks}
                    data-testid={`revoke-link-${link.id}`}
                  >
                    Revoke
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {links.length === 0 && activeLinkCount > 0 && (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Recipient links are active. Copy a link from the recipient list above.
        </p>
      )}
    </div>
  )
}

function TrackingMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
    </div>
  )
}

function PartyLinkButton({
  label,
  url,
  onCopy,
}: {
  label: string
  url: string
  onCopy: (url: string, label: string) => void
}) {
  return (
    <div className="flex max-w-full items-center gap-1 rounded-md border border-border bg-muted/30 p-1">
      <span className="min-w-0 truncate px-2 font-mono text-xs text-muted-foreground">
        {label}
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        onClick={() => onCopy(url, label)}
      >
        <Copy />
        Copy
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        onClick={() => window.open(url, '_blank', 'noreferrer')}
      >
        <ExternalLink />
        Open
      </Button>
    </div>
  )
}

function partyStatusLabel(status: PartyStatus): string {
  switch (status) {
    case 'completed':
      return 'Completed'
    case 'in_progress':
      return 'In progress'
    case 'viewed':
      return 'Viewed'
    case 'not_started':
      return 'Not started'
  }
}

function partyStatusTone(
  status: PartyStatus,
): 'neutral' | 'primary' | 'success' | 'warning' | 'outline' {
  switch (status) {
    case 'completed':
      return 'success'
    case 'in_progress':
      return 'primary'
    case 'viewed':
      return 'warning'
    case 'not_started':
      return 'neutral'
  }
}
